import { useMemo } from 'react'
import TextBundle from '@eyra/feldspar'
import { Translator } from '@eyra/feldspar'
import { TableWithContext } from './types'
import UndoSvg from './assets/images/undo.svg'

interface Props {
  table: TableWithContext
  searchedTable: TableWithContext
  handleUndo: () => void
  locale: string
}

export const TableItems = ({ table, searchedTable, handleUndo, locale }: Props): JSX.Element => {
  const text = useMemo(() => getTranslations(locale), [locale])

  const deleted = table.deletedRowCount
  const n = table.body.rows.length
  const searched = searchedTable.body.rows.length
  const total = table.originalBody.rows.length - table.deletedRowCount

  const nLabel = n.toLocaleString(locale, { useGrouping: true })
  const totalLabel = total.toLocaleString(locale, { useGrouping: true })
  const searchLabel = searched.toLocaleString(locale, { useGrouping: true })
  const deletedLabel = deleted.toLocaleString('en', { useGrouping: true }) + ' ' + text.deleted

  function getContextualLabel (): string {
    if (n === 0) return text.noData
    
    // Get contextual description based on table ID
    const contextualText = getContextualText(table.id, locale, n)
    
    if (searched < n) {
      return `${searchLabel} / ${nLabel} ${contextualText}`
    }
    return `${nLabel} ${contextualText}`
  }

  return (
    <div className='flex  min-w-[200px] gap-1'>
      <div className='flex items-center'>{tableIcon}</div>
      <div
        key={`${totalLabel}_${deleted}`}
        className='flex flex-wrap items-center px-2  gap-x-2 animate-fadeIn text-title7 md:text-title6 font-label'
      >
        <div key={totalLabel} className='animate-fadeIn'>
          {getContextualLabel()}
          {deleted > 0 ? ',' : ''}
        </div>

        <div className={`flex text-grey2 ${deleted > 0 ? '' : 'hidden'}`}>
          {deletedLabel}
          <img
            src={UndoSvg}
            className='w-5 h-5 -translate-y-[2px] md:-translate-y-0 -translate-x-[3px] ml-2'
            onClick={handleUndo}
          />
        </div>
      </div>
    </div>
  )
}

const tableIcon = (
  <svg className='h-9' viewBox='4 4 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect x='9' y='9' width='4' height='2' fill='#4272EF' />
    <rect x='9' y='13' width='4' height='2' fill='#4272EF' />
    <rect x='9' y='17' width='4' height='2' fill='#4272EF' />
    <rect x='15' y='9' width='4' height='2' fill='#4272EF' />
    <rect x='15' y='13' width='4' height='2' fill='#4272EF' />
    <rect x='15' y='17' width='4' height='2' fill='#4272EF' />
    <rect x='4' y='4' width='15' height='3' fill='#4272EF' />
    <rect x='4' y='9' width='3' height='10' fill='#4272EF' />
  </svg>
)

function getContextualText(tableId: string, locale: string, count: number): string {
  const contextualTranslations: Record<string, Record<string, { singular: string, plural: string }>> = {
    'tiktok_video_browsing_history': {
      'en': { singular: 'video watched', plural: 'videos watched' },
      'nl': { singular: 'bekeken video', plural: 'bekeken video\'s' }
    },
    'tiktok_favorite_videos': {
      'en': { singular: 'favorite video', plural: 'favorite videos' },
      'nl': { singular: 'favoriete video', plural: 'favoriete video\'s' }
    },
    'tiktok_favorite_hashtags': {
      'en': { singular: 'favorite hashtag', plural: 'favorite hashtags' },
      'nl': { singular: 'favoriete hashtag', plural: 'favoriete hashtags' }
    },
    'tiktok_hashtag': {
      'en': { singular: 'hashtag used', plural: 'hashtags used' },
      'nl': { singular: 'gebruikte hashtag', plural: 'gebruikte hashtags' }
    },
    'tiktok_like_list': {
      'en': { singular: 'liked video', plural: 'liked videos' },
      'nl': { singular: 'gelikte video', plural: 'gelikte video\'s' }
    },
    'tiktok_searches': {
      'en': { singular: 'search', plural: 'searches' },
      'nl': { singular: 'zoekopdracht', plural: 'zoekopdrachten' }
    },
    'tiktok_share_history': {
      'en': { singular: 'shared item', plural: 'shared items' },
      'nl': { singular: 'gedeeld item', plural: 'gedeelde items' }
    },
    'tiktok_settings': {
      'en': { singular: 'setting', plural: 'settings' },
      'nl': { singular: 'instelling', plural: 'instellingen' }
    },
    'tiktok_off_tiktok_activity': {
      'en': { singular: 'activity record', plural: 'activity records' },
      'nl': { singular: 'activiteitsrecord', plural: 'activiteitsrecords' }
    },
    'tiktok_followers': {
      'en': { singular: 'follower', plural: 'followers' },
      'nl': { singular: 'volger', plural: 'volgers' }
    },
    'tiktok_following': {
      'en': { singular: 'account followed', plural: 'accounts followed' },
      'nl': { singular: 'gevolgd account', plural: 'gevolgde accounts' }
    },
    'tiktok_login_history': {
      'en': { singular: 'login session', plural: 'login sessions' },
      'nl': { singular: 'inlogsessie', plural: 'inlogsessies' },
      'es': { singular: 'sesión iniciada', plural: 'sesiones iniciadas' }
    },
    'tiktok_comments': {
      'en': { singular: 'comment posted', plural: 'comments posted' },
      'nl': { singular: 'geplaatste reactie', plural: 'geplaatste reacties' },
      'es': { singular: 'comentario publicado', plural: 'comentarios publicados' }
    },
    'tiktok_activity_summary': {
      'en': { singular: 'activity statistic', plural: 'activity statistics' },
      'nl': { singular: 'activiteitsstatistiek', plural: 'activiteitsstatistieken' }
    }
  }

  const translation = contextualTranslations[tableId]?.[locale] || contextualTranslations[tableId]?.['en']
  
  if (translation) {
    return count === 1 ? translation.singular : translation.plural
  }
  
  // Fallback to generic terms
  const fallbackTranslations = getTranslations(locale)
  return fallbackTranslations.rows
}

function getTranslations (locale: string): Record<string, string> {
  const translated: Record<string, string> = {}
  for (const [key, value] of Object.entries(translations)) {
    translated[key] = Translator.translate(value, locale)
  }
  return translated
}

const translations = {
  columns: new TextBundle().add('en', 'columns').add('nl', 'kolommen'),
  rows: new TextBundle().add('en', 'rows').add('nl', 'rijen'),
  noData: new TextBundle().add('en', 'no data').add('nl', 'geen data'),
  deleted: new TextBundle().add('en', 'deleted').add('nl', 'verwijderd')
}
